import React, { useCallback, useEffect, useState } from 'react';
import CustomTooltip from '../../../components/common/tooltip';
import { ComponentConfig } from '@measured/puck';
import 'react-quill-new/dist/quill.snow.css';
import { QuillTextEditor } from '../../puck/reusable-props/quill-editor';
import { marginFields, MarginProps } from '../../puck/reusable-props/margin';
import { paddingFields, PaddingProps } from '../../puck/reusable-props/padding';
import { backgroungFields } from '../../puck/reusable-props/backgroungFields';
import {
  getcurrentDeviceType,
  getMarginStyles,
  getPaddingStyles,
  getQuillEditorText,
} from '../../utils/helper';
import {
  ReduxDispatchProps,
  ReduxSelectorProps,
} from '../../services/constants';
import { PlaceHolderField } from '../../puck/reusable-props/placeHolder';
import {
  AddItemPayload,
  CheckoutUpdateItemsPayload,
} from '../../interface/apiServiceInterface';
import { setCartData, setIsCartLoading } from '../../redux/slices/cartSlice';
import checkoutService from '../../api/checkoutService';
import { setAvailableShippingMethods } from '../../redux/slices/checkoutSlice';
import { LineItem, ShippingMethod } from '../../interface/checkoutInterface';
import { setCheckoutErrorMessage } from '../../redux/slices/commonSlice';
import { colorField, ColorProps } from '../../puck/reusable-props/color';
import { heightField, HeightProps } from '../../puck/reusable-props/height';
import { widthField, WidthProps } from '../../puck/reusable-props/width';
import {
  JustifyContentField,
  JustifyContentProps,
} from '../../puck/reusable-props/justify-content';
import { boxShadows } from '../../puck/reusable-props/options';
import { checkAndReturnWithProductVariantIdPrefix } from '../../utils/constants';

export type TicketsEntryMultiplierProps = {
  content: string;
  background: {
    color: string;
    isInherit: boolean;
  };
  boxShadow: 'none' | 'shadow-md' | 'shadow-lg' | 'shadow-xl' | 'shadow-2xl';
  placeholder: {
    add: string;
    remove: string;
  };
} & PaddingProps &
  ColorProps &
  MarginProps &
  HeightProps &
  WidthProps &
  JustifyContentProps;

export const TicketsEntryMultiplier = ({
  useAppSelector,
  useAppDispatch,
}: ReduxSelectorProps &
  ReduxDispatchProps): ComponentConfig<TicketsEntryMultiplierProps> => {
  return {
    label: (
      <CustomTooltip title="TicketsEntryMultiplier">
        <span>Tickets Entry Multiplier</span>
      </CustomTooltip>
    ) as React.ReactNode as string,
    fields: {
      content: {
        ...QuillTextEditor('Content').content,
      },
      placeholder: {
        type: 'custom',
        label: 'Place Holder',
        render: ({ value, onChange }) => {
          return (
            <PlaceHolderField
              value={value}
              onChange={onChange}
              data={[
                { label: 'Add button', value: 'add' },
                { label: 'Remove button', value: 'remove' },
              ]}
            />
          );
        },
      },
      ...marginFields,
      ...paddingFields,
      ...backgroungFields,
      ...colorField,
      ...heightField,
      ...widthField,
      ...JustifyContentField,
      boxShadow: {
        label: 'Shadow',
        type: 'select',
        options: boxShadows,
      },
    },
    defaultProps: {
      content:
        '<p class="ql-align-center" style="line-height: 1.5;"><span style="font-size: 32px;">Add A Monthly {product_entries_multiplier} </span><strong style="font-size: 32px;">x</strong><span style="font-size: 32px;"> Entry Multiplier</span></p><p class="ql-align-center" style="line-height: 1.5;"><span style="font-size: 32px;">For Just More?</span></p>  ',
      background: { color: '#ffffff', isInherit: false },
      padding: {
        deviceType: 'desktop',
        paddingBottom: { desktop: '4', tablet: '4', mobile: '4' },
        paddingLeft: { desktop: '4', tablet: '4', mobile: '4' },
        paddingRight: { desktop: '4', tablet: '4', mobile: '4' },
        paddingTop: { desktop: '4', tablet: '4', mobile: '4' },
      },
      margin: {
        desktop: { top: '4', bottom: '4', right: '0', left: '0' },
        tablet: { top: '4', bottom: '4', right: '0', left: '0' },
        mobile: { top: '4', bottom: '4', right: '0', left: '0' },
      },
      placeholder: {
        add: 'YES, ACCEPT OFFER!',
        remove: 'Remove Item',
      },
      color: {
        backgroundColor: '#0f9944',
        hoverColor: '#FFFFFF',
      },
      height: 'h-12',
      width: 'w-40',
      justifyContent: 'justify-center',
      boxShadow: 'none',
    },
    render: ({
      content,
      margin,
      padding,
      background,
      placeholder,
      color,
      justifyContent,
      height,
      width,
      boxShadow,
      editMode,
    }) => {
      const { cartData } = useAppSelector(state => state.cart);
      const VARIANT_ID = checkAndReturnWithProductVariantIdPrefix(
        cartData?.TicketEntries?.bonusVariantId || '',
      );
      const windowWidth = useAppSelector(state => state.store.currentWidth);
      const currentDeviceType = getcurrentDeviceType(windowWidth, editMode);
      const [isAddedToCart, setIsAddedToCart] = useState<boolean>(false);
      const marginStyles = getMarginStyles(margin, currentDeviceType);
      const paddingStyles = getPaddingStyles(padding, currentDeviceType);
      const dispatch = useAppDispatch();
      const isCartLoading = useAppSelector(state => state.cart.isCartLoading);
      const { pageId } = useAppSelector(state => state.checkout);
      const state = useAppSelector(state => state);
      const storeData = useAppSelector(state => state.store.storeData);
      const { preUpsellData } = useAppSelector(state => state.preUpSell);
      const newInterpolation = getQuillEditorText(content, state);

      useEffect(() => {
        if (!cartData?.LineItems?.length) {
          setIsAddedToCart(false);
          return;
        }
        const found = cartData.LineItems.some(
          (li: LineItem) => li.VariantID === VARIANT_ID,
        );
        setIsAddedToCart(found);
      }, [cartData?.LineItems?.length]);

      const handleAdd = useCallback(
        async (variantId = VARIANT_ID) => {
          dispatch(setIsCartLoading(true));
          if (!cartData?.ID || !storeData?.ShopID || !storeData?.StoreID) {
            dispatch(setIsCartLoading(false));
            return;
          }
          const lineItems = [
            {
              Quantity: 1,
              VariantID: variantId,
            },
          ];
          const payload: AddItemPayload = {
            ID: cartData?.ID,
            LineItems: lineItems,
            ShopID: storeData?.ShopID,
            StoreID: Number(storeData?.StoreID),
            PageID: String(pageId) || '',
          };

          try {
            const res = await checkoutService.checkoutAddItem(payload, {
              preUpsellData: preUpsellData,
              cartData,
            });
            if (res?.data?.Cart) {
              dispatch(setCartData(res.data.Cart));
              dispatch(
                setAvailableShippingMethods(
                  res.data.Cart.AvailableShippingMethods as ShippingMethod[],
                ),
              );
              const added = res.data.Cart.LineItems?.some(
                (li: LineItem) => li.VariantID === variantId,
              );
              setIsAddedToCart(Boolean(added));
            }
          } catch (error: unknown) {
            if (error instanceof Error) {
              dispatch(setCheckoutErrorMessage(error.message));
            }
          } finally {
            dispatch(setIsCartLoading(false));
          }
        },
        [
          cartData,
          storeData?.ShopID,
          storeData?.StoreID,
          dispatch,
          pageId,
          preUpsellData,
        ],
      );

      const handleRemove = useCallback(
        async (variantId = VARIANT_ID) => {
          dispatch(setIsCartLoading(true));
          const lineItem = cartData?.LineItems?.find(
            (item: LineItem) => item.VariantID === variantId,
          );
          const ID = lineItem?.ID;
          const lineItems = [
            {
              ID: ID,
              Quantity: 0,
              VariantID: variantId,
            },
          ];
          const payload = {
            ID: cartData?.ID,
            LineItems: lineItems,
            ShopID: storeData?.ShopID,
            StoreID: Number(storeData?.StoreID),
            PageID: String(pageId) || '',
          } as CheckoutUpdateItemsPayload;

          try {
            const res = await checkoutService.checkoutUpdateItems(
              payload,
              cartData,
            );
            if (res?.data?.Cart) {
              dispatch(setCartData(res.data.Cart));
              dispatch(
                setAvailableShippingMethods(
                  res.data.Cart.AvailableShippingMethods as ShippingMethod[],
                ),
              );
              const stillPresent = res.data.Cart.LineItems?.some(
                (li: LineItem) => li.VariantID === variantId,
              );
              setIsAddedToCart(Boolean(stillPresent));
            }
          } catch (error: unknown) {
            if (error instanceof Error) {
              dispatch(setCheckoutErrorMessage(error?.message));
            }
          } finally {
            dispatch(setIsCartLoading(false));
          }
        },
        [cartData, storeData?.ShopID, storeData?.StoreID, dispatch, pageId],
      );

      if (!cartData?.TicketEntries?.bonusVariantId) {
        return <></>;
      }

      return (
        <>
          <div style={{ ...marginStyles }}>
            <div
              className={`text-editor-content  `}
              style={{
                ...paddingStyles,
                backgroundColor: background?.isInherit
                  ? 'inherit'
                  : background?.color,
              }}>
              <div dangerouslySetInnerHTML={{ __html: newInterpolation }} />
              <div className={`flex ${justifyContent} mt-3`}>
                {isAddedToCart ? (
                  <button
                    onClick={() => {
                      handleRemove();
                    }}
                    style={{ backgroundColor: color?.backgroundColor }}
                    onMouseOver={e => {
                      e.currentTarget.style.backgroundColor = color?.hoverColor;
                    }}
                    onMouseOut={e => {
                      e.currentTarget.style.backgroundColor =
                        color?.backgroundColor;
                    }}
                    disabled={
                      isCartLoading || cartData?.LineItems?.length === 1
                    }
                    type="button"
                    className={`py-1.5 mt-1 px-3 !text-black bg-page_builder_theme  ${height} ${width} ${boxShadow} text-sm font-semibold rounded-md `}>
                    {placeholder?.remove || 'Remove'}
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      handleAdd();
                    }}
                    disabled={isCartLoading}
                    style={{ backgroundColor: color?.backgroundColor }}
                    onMouseOver={e => {
                      e.currentTarget.style.backgroundColor = color?.hoverColor;
                    }}
                    onMouseOut={e => {
                      e.currentTarget.style.backgroundColor =
                        color?.backgroundColor;
                    }}
                    type="button"
                    className={`w-auto py-1.5 mt-1 px-3 !text-black bg-page_builder_theme  ${height} ${width} ${boxShadow} text-sm font-semibold rounded-md focus:outline-none`}>
                    {placeholder?.add || 'Add'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </>
      );
    },
  };
};

export default TicketsEntryMultiplier;
